import { IoCloseCircleSharp } from "react-icons/io5";
import {
  addItem,
  useFetchItemDetailsQuery,
  useTypedDispatch,
} from "../../store";
import Modal from "../common/Modal";
import ModifierGroup from "./ModifierGroup";
import { useState } from "react";
import { Item, ItemCustomization } from "../../types";
import Button from "../common/Button";
import { formatPrice } from "../../utils/helpers";
import Skeleton from "../common/Skeleton";

const ItemDetailsWrapper: React.FC<{
  itemId: string;
  onClose: () => void;
}> = ({ itemId, onClose }) => {
  const {
    data: itemDetails,
    error,
    isFetching: isFetching,
  } = useFetchItemDetailsQuery(itemId);

  if (isFetching) {
    return (
      <Modal>
        <div className="w-full relative bg-white md:max-w-4xl h-full md:h-auto md:max-h-[90vh] md:rounded-2xl flex flex-col md:flex-row overflow-hidden shadow-2xl">
          <div className="h-[300px] md:h-auto w-full md:w-1/2 bg-gray-100 relative">
            <Skeleton
              times={1}
              className="h-full w-full absolute inset-0"
            />
          </div>
          <div className="flex flex-col h-full w-full md:w-1/2 p-6 gap-4">
            <Skeleton
              times={1}
              className="h-10 w-3/4 rounded-lg"
            />
            <Skeleton
              times={1}
              className="h-8 w-1/4 rounded-lg"
            />
            <Skeleton
              times={3}
              className="h-4 w-full rounded"
            />
            <div className="w-full h-px bg-gray-100 my-4"></div>
            <Skeleton
              times={2}
              className="h-20 w-full rounded-xl"
            />
            <Skeleton
              times={1}
              className="h-14 w-full mt-auto rounded-xl"
            />
          </div>
        </div>
      </Modal>
    );
  }

  if (!itemDetails || error) {
    return (
      <Modal>
        <div className="bg-white w-full h-full flex items-center justify-center p-8">
          <div className="text-center">
            <h3 className="text-xl font-bold text-gray-800 mb-2">
              Une erreur s&apos;est produite
            </h3>
            <p className="text-gray-500">
              Impossible de charger les détails de l&apos;article.
            </p>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <ItemDetails
      itemDetails={itemDetails}
      onClose={onClose}
    />
  );
};

const ItemDetails: React.FC<{
  itemDetails: Required<Item>;
  onClose: () => void;
}> = ({ itemDetails, onClose }) => {
  const [itemCustomization, setItemCustomization] = useState<ItemCustomization>(
    {
      _id: itemDetails._id,
      displayName: itemDetails.displayName,
      qty: 1,
      imagePath: itemDetails.imagePath,
      price: itemDetails.price,
      modifierGroups: [],
    }
  );

  const dispatch = useTypedDispatch();

  const renderedModifierGroups = itemDetails.modifierGroups.map(
    (modifierGroup) => {
      return (
        <ModifierGroup
          key={modifierGroup._id}
          modifierGroup={modifierGroup}
          itemCustomization={itemCustomization}
          setItemCustomization={setItemCustomization}
        />
      );
    }
  );

  const getTotalModifierOptionsByModifierGroup = (
    itemCustomization: ItemCustomization,
    modifierGroupId: string
  ): number => {
    const targetGroup = itemCustomization.modifierGroups.find(
      (modGroupElem) => modGroupElem._id === modifierGroupId
    );

    if (!targetGroup) return 0;
    return targetGroup.modifierOptions.reduce(
      (total, option) => total + (option.qty || 1),
      0
    );
  };

  const isAddItemDisabled = (): boolean => {
    for (let i = 0; i < itemDetails.modifierGroups.length; i++) {
      const modGroupElem = itemDetails.modifierGroups[i];

      const totalSelectModifierOptions = getTotalModifierOptionsByModifierGroup(
        itemCustomization,
        modGroupElem._id
      );
      const { minPermitted, maxPermitted } = modGroupElem.quantityInfo;
      if (
        totalSelectModifierOptions < minPermitted ||
        minPermitted > maxPermitted
      ) {
        return true;
      }
    }

    return false;
  };

  const totalModifierOptionsPrice = itemCustomization.modifierGroups.reduce(
    (groupAcc, modGroupElem) => {
      const groupTotal = modGroupElem.modifierOptions.reduce(
        (optionAcc, modOptionElem) => {
          return optionAcc + modOptionElem.price.value * modOptionElem.qty;
        },
        0
      );
      return groupAcc + groupTotal;
    },
    0
  );

  const totalCustomizationItemPrice: number =
    itemCustomization.price.value + totalModifierOptionsPrice;

  const handleAddItemToCart = () => {
    dispatch(
      addItem({
        item: itemCustomization,
        restaurantId: itemDetails.restaurant,
        totalPrice: totalCustomizationItemPrice,
      })
    );
    onClose();
  };

  return (
    <Modal
      onClose={onClose}
      fullscreen
    >
      <div className="w-full h-full md:h-auto md:max-h-[90vh] md:max-w-5xl bg-white md:rounded-2xl flex flex-col md:flex-row overflow-hidden shadow-2xl relative mx-auto my-4">
        <button
          onClick={onClose}
          className="absolute right-4 top-4 z-50 bg-white/80 hover:bg-white p-2 rounded-full backdrop-blur-sm transition-colors shadow-sm"
        >
          <IoCloseCircleSharp className="text-gray-800 w-8 h-8" />
        </button>

        <div className="h-[250px] md:h-auto w-full md:w-1/2 bg-gray-50 relative flex-shrink-0 flex items-center justify-center p-4">
          <img
            src={itemDetails.imagePath}
            alt={itemDetails.displayName}
            className="max-w-full max-h-full w-auto h-auto object-contain rounded-lg"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent md:hidden pointer-events-none" />
        </div>

        <div className="flex flex-col flex-1 w-full md:w-1/2 bg-white min-h-0">
          <div className="flex-1 overflow-y-auto px-6 py-8">
            <div className="flex flex-col gap-3 mb-8">
              <h2 className="font-bold text-3xl text-gray-900 leading-tight">
                {itemDetails.displayName}
              </h2>
              <div className="font-semibold text-2xl text-primary">
                {itemDetails.price.text}
              </div>
              {itemDetails.description && (
                <p className="text-gray-600 leading-relaxed text-base">
                  {itemDetails.description}
                </p>
              )}
            </div>

            {renderedModifierGroups.length > 0 && (
              <>
                <div className="w-full h-px bg-gray-100 my-6"></div>
                <div className="flex flex-col gap-8 pb-4">
                  {renderedModifierGroups}
                </div>
              </>
            )}
          </div>

          <div className="p-4 md:p-6 border-t border-gray-100 bg-white mt-auto flex-shrink-0 z-20">
            <Button
              className={`
                w-full justify-center font-bold text-lg py-4 px-6 rounded-xl transition-all transform active:scale-[0.98]
                ${
                  isAddItemDisabled()
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-black text-white hover:bg-gray-900 shadow-lg hover:shadow-xl"
                }
              `}
              disabled={isAddItemDisabled()}
              onClick={handleAddItemToCart}
            >
              <div className="flex items-center justify-between w-full">
                <span>Ajouter au panier</span>
                <span className="bg-white/20 px-3 py-1 rounded-lg text-base">
                  {formatPrice(totalCustomizationItemPrice)}
                </span>
              </div>
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ItemDetailsWrapper;
